import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Box,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { AlertTriangle, CheckCircle2, XCircle, MinusCircle, Sparkles } from 'lucide-react';
import { EpaFinding } from '@/types';

interface EpaFindingAccordionProps {
  finding: EpaFinding;
  index: number;
  expandedPanel: string | false;
  onAccordionChange: (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  onAssignTask: (findingId: string) => void;
  isTaskDisabled?: boolean;
}

export function EpaFindingAccordion({
  finding,
  index,
  expandedPanel,
  onAccordionChange,
  onAssignTask,
  isTaskDisabled = false,
}: EpaFindingAccordionProps) {
  const theme = useTheme();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'PASS':
        return <CheckCircle2 size={16} color={theme.palette.success.main} />;
      case 'FAIL':
        return <XCircle size={16} color={theme.palette.error.main} />;
      case 'PARTIAL':
        return <MinusCircle size={16} color={theme.palette.warning.main} />;
      default:
        return <MinusCircle size={16} color={theme.palette.grey[500]} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PASS':
        return theme.palette.success.main;
      case 'FAIL':
        return theme.palette.error.main;
      case 'PARTIAL':
        return theme.palette.warning.main;
      default:
        return theme.palette.grey[500];
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PASS':
        return 'Pass';
      case 'FAIL':
        return 'Fail';
      case 'PARTIAL':
        return 'Partial';
      default:
        return status;
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return '#dc2626';
      case 'high':
        return '#ef4444';
      case 'medium':
        return '#f59e0b';
      case 'low':
        return '#22c55e';
      default:
        return theme.palette.grey[500];
    }
  };

  const panelId = `epa-panel${index}`;
  const isExpanded = expandedPanel === panelId;

  return (
    <Accordion
      expanded={isExpanded}
      onChange={onAccordionChange(panelId)}
      elevation={0}
      disableGutters
      sx={{
        border: '1px solid',
        borderColor: isExpanded ? alpha(getStatusColor(finding.status), 0.3) : 'divider',
        borderRadius: '8px !important',
        '&:before': { display: 'none' },
        mb: 1,
        overflow: 'hidden',
        transition: 'border-color 0.2s',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          minHeight: 56,
          px: 2,
          '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' },
          bgcolor: isExpanded ? alpha(getStatusColor(finding.status), 0.03) : 'transparent',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, mr: 1 }}>
          {getStatusIcon(finding.status)}
          <Typography sx={{ fontSize: 13, fontWeight: 500, flex: 1 }}>
            {finding.finding}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Chip
              label={finding.severity.toUpperCase()}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 600,
                bgcolor: alpha(getSeverityColor(finding.severity), 0.1),
                color: getSeverityColor(finding.severity),
                borderRadius: 1,
              }}
            />
            <Chip
              label={finding.category}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 500,
                bgcolor: alpha('#6366f1', 0.08),
                color: '#6366f1',
                borderRadius: 1,
              }}
            />
            {finding.status === 'FAIL' && (
              <Tooltip title={isTaskDisabled ? 'Task in progress' : 'Assign Task'}>
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onAssignTask(finding.id);
                    }}
                    disabled={isTaskDisabled || finding.hasActiveTask}
                    sx={{
                      ml: 0.5,
                      color: '#8b5cf6',
                      '&:hover': { bgcolor: alpha('#8b5cf6', 0.1) },
                    }}
                  >
                    <Sparkles size={16} />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
        <Divider sx={{ mb: 1.5 }} />

        {/* Status */}
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
          <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>Status:</Typography>
          <Chip
            icon={getStatusIcon(finding.status)}
            label={getStatusLabel(finding.status)}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              fontWeight: 500,
              bgcolor: alpha(getStatusColor(finding.status), 0.1),
              color: getStatusColor(finding.status),
              '& .MuiChip-icon': { ml: 0.5 },
            }}
          />
          {finding.forceMark && (
            <Tooltip title={`Marked by ${finding.forcedBy || 'user'} on ${finding.forcedAt || 'unknown'}: ${finding.forceJustification || ''}`}>
              <Chip
                label="Force Marked"
                size="small"
                sx={{ height: 20, fontSize: 10, bgcolor: alpha('#f59e0b', 0.1), color: '#f59e0b' }}
              />
            </Tooltip>
          )}
        </Stack>

        {/* File */}
        {finding.file && (
          <Box mb={1.5}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', mb: 0.25 }}>
              Affected File
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'primary.main', fontFamily: 'monospace' }}>
              {finding.file}{finding.lineRange ? `:${finding.lineRange}` : ''}
            </Typography>
          </Box>
        )}

        {/* Details */}
        {finding.details && (
          <Box mb={1.5}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', mb: 0.25 }}>
              Details
            </Typography>
            <Typography sx={{ fontSize: 12, color: 'text.primary', whiteSpace: 'pre-wrap' }}>
              {finding.details}
            </Typography>
          </Box>
        )}

        {/* Evidence */}
        {finding.evidence && finding.evidence.length > 0 && (
          <Box mb={1.5}>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
              Evidence ({finding.evidence.length})
            </Typography>
            <Stack spacing={0.75}>
              {finding.evidence.map((ev, i) => (
                <Paper
                  key={i}
                  elevation={0}
                  sx={{
                    p: 1,
                    bgcolor: alpha('#000', 0.02),
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Typography sx={{ fontSize: 11, fontFamily: 'monospace', color: 'primary.main' }}>
                    {ev.file}:{ev.line_range}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: 'text.secondary', mt: 0.25 }}>
                    {ev.description}
                  </Typography>
                  {ev.code_snippet && (
                    <Box
                      sx={{
                        mt: 0.5,
                        p: 0.75,
                        bgcolor: alpha('#000', 0.04),
                        borderRadius: 0.5,
                        fontFamily: 'monospace',
                        fontSize: 10,
                        whiteSpace: 'pre-wrap',
                        overflow: 'auto',
                      }}
                    >
                      {ev.code_snippet}
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
          </Box>
        )}

        {/* Recommendations (for FAIL/PARTIAL) */}
        {finding.recommendations && finding.recommendations.length > 0 && (
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'warning.main', mb: 0.5 }}>
              Recommendations ({finding.recommendations.length})
            </Typography>
            <Stack spacing={0.5}>
              {finding.recommendations.map((r, i) => (
                <Stack key={i} direction="row" spacing={0.75} alignItems="flex-start">
                  <AlertTriangle size={12} color={theme.palette.warning.main} style={{ marginTop: 2, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 12, color: 'text.primary' }}>{r}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
